import { Link } from "react-router-dom";
import { categoryOf, conditionLabel, isUSNew, newOptionsFor, textKey, storageKey, appleUntil, formatPrice } from "@/lib/store";
import { NewOptions } from "./NewOptions";

const STORAGE_ORDER = ["32gb", "64gb", "128gb", "256gb", "512gb", "1tb", "2tb"];
const sOrder = (s) => STORAGE_ORDER.indexOf(storageKey(s));
const familyOf = (model = "") => textKey(model).match(/^(.*?\d+)/)?.[1] || textKey(model);
const byStorage = (a, b) => sOrder(a.storage) - sOrder(b.storage);
const sameStorageAs = (a, b) => storageKey(a) === storageKey(b);
// O que diferencia aparelhos parecidos: bateria e garantia Apple; se ainda empatar, o preço.
const unitBits = (p) => [p.battery != null ? `Bateria ${p.battery}%` : null, appleUntil(p) ? `Apple até ${appleUntil(p)}` : null];
const labelsFor = (list, base) => {
  const first = list.map((p) => [...base(p), ...unitBits(p)].filter(Boolean).join(" · "));
  return list.map((p, i) => (first.filter((l) => l === first[i]).length > 1 && p.price > 0 ? [first[i], formatPrice(p.price)].filter(Boolean).join(" · ") : first[i]));
};
const optionBase = (p) => [p.color, conditionLabel(p)];

const pick = (list, current) =>
  list.find((p) => sameStorageAs(p.storage, current.storage) && p.condition === current.condition) ||
  list.find((p) => sameStorageAs(p.storage, current.storage)) ||
  list.slice().sort(byStorage)[0];

const Row = ({ label, children, testid }) => (
  <div data-testid={testid}>
    <p className="mono mb-2 text-[11px] uppercase tracking-[0.22em] text-zinc-400">{label}</p>
    <div className="flex flex-wrap gap-2">{children}</div>
  </div>
);

const Option = ({ to, active, children, testid }) => (
  <Link to={to} replace data-testid={testid} aria-current={active ? "true" : undefined}
    className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors duration-300 ${active ? "border-white bg-white text-black" : "border-white/15 text-zinc-200 hover:border-white/50 hover:text-white"}`}>
    {children}
  </Link>
);

const accKey = (p) => (p.model || "").toLowerCase().replace((p.color || "").toLowerCase(), "").replace(/\s+/g, " ").trim();
const accLabel = (p, i) => [p.color, p.wattage].filter(Boolean).join(" · ") || `Opção ${i + 1}`;

const AccessorySwitcher = ({ product, pool }) => {
  const same = pool
    .filter((p) => categoryOf(p) === "acessorios" && accKey(p) === accKey(product))
    .sort((a, b) => (a.color || "").localeCompare(b.color || "") || (a.wattage || "").localeCompare(b.wattage || ""));
  if (same.length < 2) return null;
  return (
    <div data-testid="variant-switcher" className="mt-8 rounded-[24px] border border-white/10 bg-white/[0.03] p-5">
      <Row label="Cor" testid="variant-colors">
        {same.map((p, i) => <Option key={p.id} to={`/produto/${p.id}`} active={p.id === product.id} testid={`variant-color-${p.id}`}>{accLabel(p, i)}</Option>)}
      </Row>
    </div>
  );
};

export const VariantSwitcher = ({ product, all }) => {
  if (!product) return null;
  const pool = [product, ...(Array.isArray(all) ? all : []).filter((p) => p.id !== product.id && p.status === "disponivel" && categoryOf(p) === categoryOf(product))];
  if (categoryOf(product) === "acessorios") return <AccessorySwitcher product={product} pool={pool} />;
  const family = pool.filter((p) => familyOf(p.model) === familyOf(product.model));
  const modelKeys = [...new Set(family.map((p) => textKey(p.model)))].sort((a, b) => a.length - b.length || a.localeCompare(b));
  const ofModel = (k) => family.filter((p) => textKey(p.model) === k);
  const current = textKey(product.model);
  const sameModel = ofModel(current);
  const storages = sameModel.map((p) => p.storage).filter(Boolean)
    .filter((s, i, arr) => arr.findIndex((x) => sameStorageAs(x, s)) === i)
    .sort((a, b) => sOrder(a) - sOrder(b));
  const sameStorage = sameModel.filter((p) => sameStorageAs(p.storage, product.storage));
  const full = isUSNew(product) ? newOptionsFor(product.model) : null;
  // Novos: várias unidades da mesma cor e GB viram uma linha própria para o cliente escolher a unidade.
  const twins = full ? sameStorage.filter((p) => textKey(p.color) === textKey(product.color) && p.condition === product.condition) : [];
  const optionLabels = labelsFor(sameStorage, optionBase);
  const twinLabels = labelsFor(twins, () => []);

  if (!full && modelKeys.length < 2 && storages.length < 2 && sameStorage.length < 2) return null;

  return (
    <div data-testid="variant-switcher" className="mt-8 space-y-5 rounded-[24px] border border-white/10 bg-white/[0.03] p-5">
      {modelKeys.length > 1 && (
        <Row label="Modelo" testid="variant-models">
          {modelKeys.map((k) => {
            const target = k === current ? product : pick(ofModel(k), product);
            return <Option key={k} to={`/produto/${target.id}`} active={k === current} testid={`variant-model-${k.replace(/\s+/g, "-")}`}>{target.model}</Option>;
          })}
        </Row>
      )}
      {full && <NewOptions product={product} sameModel={sameModel} options={full} />}
      {full && twins.length > 1 && (
        <Row label="Escolha a unidade" testid="variant-units">
          {twins.map((p, i) => <Option key={p.id} to={`/produto/${p.id}`} active={p.id === product.id} testid={`variant-unit-${p.id}`}>{twinLabels[i] || `Unidade ${i + 1}`}</Option>)}
        </Row>
      )}
      {!full && storages.length > 1 && (
        <Row label="Armazenamento" testid="variant-storages">
          {storages.map((s) => {
            const active = sameStorageAs(s, product.storage);
            const target = active ? product : pick(sameModel.filter((p) => sameStorageAs(p.storage, s)), { ...product, storage: s });
            return <Option key={s} to={`/produto/${target.id}`} active={active} testid={`variant-storage-${storageKey(s)}`}>{s}</Option>;
          })}
        </Row>
      )}
      {!full && sameStorage.length > 1 && (
        <Row label="Cor e condição" testid="variant-options">
          {sameStorage.map((p, i) => <Option key={p.id} to={`/produto/${p.id}`} active={p.id === product.id} testid={`variant-option-${p.id}`}>{optionLabels[i]}</Option>)}
        </Row>
      )}
    </div>
  );
};
